import { MediaInfo } from '@src/modules/media/interfaces/MediaInfo.interface';
import { ChannelTypeEnum } from 'talkplus-sdk';
import { GetPlayerQueryDto } from '@src/modules/players/shared/dtos/player.dto';

export enum PlayerTypeEnum {
  JW,
  FLOW,
}

export interface PlayerConfig {
  language?: string;
  x_midibus_uuid?: string;
  x_midibus_uuids?: string[];
  x_midibus_ch_id?: string;
  x_midibus_ob_id?: string;
  x_midibus_st_id?: string;
  x_midibus_userid?: string;
  x_midibus_evt_id?: string;
  x_midibus_sec_event_id?: string;
  x_midibus_k?: string;
  x_midibus_etc_param?: string;
  label?: string;
  message?: string;
  isErrored?: boolean;
  logo?: FlowLogo | JwLogo;
  skin?: PlayerSkin;
  volume?: number;
  progressbarColor?: string; // 기존에 이 옵션 쓰던 곳 있어서 유지
  customOption?: CustomOption;
}

export interface SimplePlayerConfig extends PlayerConfig {
  controls?: boolean;
  source?: { src: string; type?: string };
  poster?: string | null;
  autoplay?: boolean;
  muted?: boolean;
  loop?: boolean;
}

type FlowLogo = string;

export type JwLogo = {
  file: string;
  position?: LogoPosition;
};

export enum DRM {
  DISABLE,
  KINX_DRM,
  MULTI_DRM,
}

export interface CustomOption {
  enableChat?: boolean;
  uiOption: PlayerUiOption;
  drm: DRM;
  fullscreen?: boolean;
  trackWatchedSections?: boolean;
  logoPosition?: LogoPosition;
  onlyProgress?: boolean;
  rewind?: boolean;
  playFullscreen?: boolean;
  seekStep?: number;
  mobileSizing?: boolean;
  mouseoverPlay?: boolean;
  speeds?: number[];
  volume?: number;
  posterShadow?: boolean;
  allowRightClick?: boolean;
  noControls?: boolean;
  noSkip?: boolean;
  noSkipForward?: boolean;
  startTime?: number;
  stretching?: string;
  related?: boolean | { displayMode?: string; [key: string]: any };
  noControlbar?: boolean;
  exposeSpeedIcon?: boolean;
  prevObject?: string | PrevObjectPlaylist;
  abLoop?: boolean;
  watermark?: Watermark;
  createSection?: boolean;
  disableAutoplayMute?: boolean;
  addSkipBtn?: boolean;
  screenLock?: boolean;
  cachePlaybackRate?: boolean;
  clickToPlayPause?: boolean;
  outroObject?: string | { playlist: JwPlaylistSource[] };
  pipIcon?: 'enabled' | 'disabled';
  fullscreenOrientationLock?: 'none' | 'landscape' | 'portrait';
  previewObject?: string | MediaInfo;
  /** true일 때 HLS 세그먼트(ts)를 재생 전에 미리 로드 */
  autoload?: boolean;
  /**
   * Safari/iOS에서 JW 기본 화질 메뉴가 없을 때 커스텀 화질 메뉴를 노출한다.
   * 옵트인 — true로 켠 채널에서만 동작한다.
   */
  appleCustomQuality?: boolean;
  /**
   * 버퍼링 스피너를 즉시 띄우지 않고, 버퍼링이 일정 시간 이상 지속될 때만 노출한다.
   * 이미 버퍼된 구간으로 seek할 때 스피너가 깜빡이는 현상을 막는다.
   * 기본 활성 — 옛 동작이 필요한 채널만 false로 명시해 끈다.
   */
  deferBufferSpinner?: boolean;
  drmSecurityLevel?: DrmSecurityLevel;
}

export interface DrmSecurityLevel {
  widevine?: {
    videoRobustness?: DrmRobustness;
    audioRobustness?: DrmRobustness;
  };
  playready?: {
    videoRobustness?: DrmRobustness;
    audioRobustness?: DrmRobustness;
  };
}

export type PrevObjectPlaylist = MediaInfo & {
  originalPlaylist?: JwPlaylistSource[];
};

export interface Watermark {
  type?: 'random' | 'duplicate';
  amount?: number;
  /** 캔버스 크기 (width == height) */
  size?: number;
  /** 폰트 크기(px) */
  fontSize?: number;
  /** 폰트 패밀리 */
  fontFamily?: string;
  /** 텍스트 색상 (rgba) */
  color?: string;
  /** 회전 각도(도 단위, 시계방향 양수) */
  angleDeg?: number;
  enable: boolean;
  label: string;
  randomTiming?: number;
}

export interface PlayerSkin {
  logoPosition?: LogoPosition;
  controlbar?: {
    text: string;
    icons: string;
    iconsActive: string;
    background: string;
  };
  // 아래 두 개 같은 건지 확인 필요
  progressbarColor?: string;
  timeslider?: { progress?: string; [key: string]: any };
  playIconUrl?: string;
  customIcon?: {
    play?: string;
    pause?: string;
    fullscreen?: string;
    settings?: string;
    volume?: string;
  };
  name: string;
  captions?: {
    backgroundColor?: string;
    backgroundOpacity?: number;
    color?: string;
    fontFamily?: string;
    fontUrl?: string;
    fontOpacity?: number;
    fontSize?: number;
    windowColor?: string;
    windowOpacity?: number;
    letterSpacing?: string;
  };
  playIconCustomCss?: string;
}

export interface YoziiPlayerConfig extends PlayerConfig {
  clip?: {
    src: string;
    token: string;
  };
  customOption?: CustomOption & {
    watermark?: boolean;
  };
  token?: string;
  ensubtitleurl?: string;
  timestamp?: number;
  x_midibus_yozii_owner_param?: string;
  loop?: boolean;
  autoplay?: boolean;
  muted?: boolean;
}
export interface FlowPlayerConfig extends PlayerConfig {
  chromecast?: boolean;
  share?: boolean; // false로 안하면 공유 버튼 생성됨
  ratio?: number; // 2로 해야 전체화면 꽉 차게 생성됨
  clip?: FlowClip;
  disabled?: boolean;
  seekable?: boolean;
  keyboard?: boolean;
  volume?: number;
  speeds?: number[];
  loop?: boolean;
  muted?: boolean;
  autoplay?: boolean;
  poster?: string;
  logo?: FlowLogo;
  key?: string;
}

export interface FlowClip {
  vodQualities?: {
    template?: string;
    qualities?: string[];
  };
  sources?: FlowMediaSource[];
  subtitles?: any[];
}

export interface FlowMediaSource {
  type?: string;
  src?: string;
}

export interface JWPlayerConfig extends PlayerConfig {
  captions?: any;
  logo?: JwLogo;
  height?: string | number;
  width?: string | number;
  stretching?: string;
  aspectratio?: null | string;
  responsive?: boolean;
  repeat?: boolean;
  autostart?: boolean;
  mute?: boolean;
  playbackRateControls?: boolean;
  playbackRates?: number[];
  fullscreen?: boolean;
  allowFullscreen?: boolean;
  native_fullscreen?: boolean;
  chromecast?: boolean;
  share?: boolean;
  ratio?: number;
  title?: string;
  tracks?: jwplayer.Track[];
  playlist?: JwPlaylistSource[];
  related?: boolean | { displayMode?: string; [key: string]: any };
  controls?: boolean;
  watermark?: Watermark;
  playButton?: string; // 이전에 쓰던거 남아있는거.. 나중에 없애기
  pipIcon?: 'enabled' | 'disabled';
  nextUpDisplay?: boolean;
  fullscreenOrientationLock?: 'landscape' | 'none' | 'portrait';
  plugins?: any;
  x_midibus_app_id?: string;
  x_midibus_chatting?: ChannelTypeEnum;
  x_midibus_published?: string;
  chapterUrl?: string;
}

export interface JWLivePlayerConfig extends JWPlayerConfig {
  playlist: JwLiveSource[];
}

export enum LogoPosition {
  TOP_LEFT = 'top-left',
  TOP_RIGHT = 'top-right',
  BOTTOM_RIGHT = 'bottom-right',
  BOTTOM_LEFT = 'bottom-left',
  CONTROL_BAR = 'control-bar',
}

export type JwplayerCustomOption = CustomOption;

export interface JwThumbnail {
  file: string;
  kind: 'thumbnails';
}
export interface JwCaption {
  kind: 'captions';
  default: boolean;
  file: string;
  label: string;
}

export interface JwChapterMarker {
  kind: 'chapters';
  file: string;
}

export interface JwMediaSource {
  file: string;
  type?: string;
  default?: boolean;
  drm?:
    | {
        widevine: WideVineDrmConfig;
        playready: PlayreadyDrmConfig;
      }
    | {
        fairplay: FairplayDrmConfig;
      };
  label?: string;
  playable?: boolean;
}

export interface JwMediaSourceFairPlay extends JwMediaSource {
  drm: {
    fairplay: FairplayDrmConfig;
  };
}

export interface JwMediaSourceWideVinePlayready extends JwMediaSource {
  drm: {
    widevine: WideVineDrmConfig;
    playready: PlayreadyDrmConfig;
  };
}

export type JwPlaylistSource = JwProgressiveSource | JwHlsSource | JwLiveSource;

export type JwPlaylistSourceBase = {
  image?: string;
  tracks?: (JwThumbnail | JwCaption | JwChapterMarker)[];
  title?: string;
};

export type JwProgressiveSource = JwPlaylistSourceBase & {
  sources: JwMediaSource[];
};

export type JwHlsSource = JwPlaylistSourceBase & {
  file: string;
};

export type JwLiveSource = JwPlaylistSourceBase & {
  sources: JwMediaSource[];
};

export interface PallyconDrmHeader {
  name: string;
  value: string;
}
export type DrmRobustness =
  | 'HW_SECURE_ALL'
  | 'HW_SECURE_CRYPTO'
  | 'HW_SECURE_DECODE'
  | 'SW_SECURE_CRYPTO'
  | 'SW_SECURE_DECODE';

export interface WideVineDrmConfig {
  url: string;
  headers: PallyconDrmHeader[];
  videoRobustness?: DrmRobustness;
  audioRobustness?: DrmRobustness;
}
export interface PlayreadyDrmConfig {
  url: string;
  headers: PallyconDrmHeader[];
  videoRobustness?: DrmRobustness;
  audioRobustness?: DrmRobustness;
}
export interface FairplayDrmConfig {
  certificateUrl: string;
  processSpcUrl: string;
  licenseRequestHeaders: PallyconDrmHeader[];
  licenseResponseType: string;
  extractContentId?: (url: string) => string;
  licenseRequestMessage?: (message: string, session: unknown) => string;
  extractKey?: (response: string) => Uint8Array;
}

export interface RequestInfo {
  ip?: string;
  headers: { [key: string]: any };
  query: GetPlayerQueryDto;
}

// 플레이어 UI 옵션 설정 : 전체화면 / 화질 / 배속
export interface PlayerUiOption {
  fullscreen: boolean;
  speed: boolean;
  quality: boolean;
}
